import React, { useRef, useEffect, useState } from 'react';
import { Badge } from './ui/badge';

interface ServiceItemProps {
  title: string;
  description: string;
  image: string;
  link: string;
}

interface ServicesProps {
  title: string;
  items: ServiceItemProps[];
}

const ServiceCard: React.FC<ServiceItemProps> = ({ title, description, image, link }) => (
  <a href={link} className="block group shadow-lg hover:shadow-2xl transition-shadow duration-300">
    <div className="relative h-64 w-full">
      <div className="absolute inset-0 rounded-lg z-10 transform transition-transform duration-300 group-hover:scale-105">
        <div className="w-full h-full rounded-lg bg-gradient-to-t from-black/70 to-transparent opacity-80 group-hover:opacity-90 transition-opacity duration-300" />
        </div>
      <img
        src={image}
        alt={title}
        className="object-cover w-full h-full rounded-lg z-0 transform transition-transform duration-300 group-hover:scale-105"
      />
      <div className="absolute bottom-0 left-0 right-0 p-6 text-white z-20">
        <h3 className="text-2xl font-bold mb-2">{title}</h3>
        <p className="text-sm opacity-90">{description}</p>
      </div>
    </div>
  </a>
);

const Services: React.FC<ServicesProps> = ({ title, items }) => {
  const [visible, setVisible] = useState(false);
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new window.IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          observer.disconnect();
}
      },
      { threshold: 0.2 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section
      id="services"
      ref={sectionRef}
      className={`py-20 lg:py-40 bg-gray-200 transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
    >
      <div className="container mx-auto px-4">
        <div className="text-center mb-16">
          <h2 className="text-4xl font-bold mb-4">{title}</h2>
          <div className="w-24 h-1 bg-primary-500 mx-auto"></div>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {items.map((service, index) => (
            <ServiceCard key={index} {...service} />
          ))}
        </div>
      </div>
    </section>
  );
};

export default Services; 